import axios from 'axios';
import {
  EmployeePreferences,
  DatePreference,
  DayPreference,
  TimePreference,
  ShiftTypePreference,
  CreateDatePreferenceData,
  CreateDayPreferenceData,
  CreateTimePreferenceData,
  CreateShiftTypePreferenceData,
  UpdateDatePreferenceData,
  UpdateDayPreferenceData,
  UpdateTimePreferenceData,
  UpdateShiftTypePreferenceData,
  PreferenceConflict,
  PreferenceOverride,
  CreatePreferenceOverrideData,
  PreferenceTemplate,
  CreatePreferenceTemplateData
} from '@/types/preference';

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || '/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken,
          });
          
          const { accessToken, refreshToken: newRefreshToken } = response.data;
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', newRefreshToken);
          
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export class PreferenceService {
  // Get current user's preferences
  static async getMyPreferences(): Promise<EmployeePreferences> {
    const response = await apiClient.get('/preferences/my-preferences');
    return response.data;
  }

  // Get employee preferences (for managers)
  static async getEmployeePreferences(employeeId: number): Promise<EmployeePreferences> {
    const response = await apiClient.get(`/preferences/employee/${employeeId}`);
    return response.data;
  }

  // Get all user preferences (for managers/admins)
  static async getAllUserPreferences(): Promise<{ employee_id: number; preferences: EmployeePreferences }[]> {
    const response = await apiClient.get('/preferences/all-users');
    return response.data;
  }

  // Date Preferences
  static async createDatePreference(data: CreateDatePreferenceData): Promise<DatePreference> {
    const response = await apiClient.post('/preferences/date', data);
    return response.data;
  }

  static async updateDatePreference(id: number, data: UpdateDatePreferenceData): Promise<DatePreference> {
    const response = await apiClient.put(`/preferences/date/${id}`, data);
    return response.data;
  }

  static async deleteDatePreference(id: number): Promise<void> {
    await apiClient.delete(`/preferences/date/${id}`);
  }

  // Day Preferences
  static async createDayPreference(data: CreateDayPreferenceData): Promise<DayPreference> {
    const response = await apiClient.post('/preferences/day', data);
    return response.data;
  }

  static async updateDayPreference(id: number, data: UpdateDayPreferenceData): Promise<DayPreference> {
    const response = await apiClient.put(`/preferences/day/${id}`, data);
    return response.data;
  }

  static async deleteDayPreference(id: number): Promise<void> {
    await apiClient.delete(`/preferences/day/${id}`);
  }

  // Time Preferences
  static async createTimePreference(data: CreateTimePreferenceData): Promise<TimePreference> {
    const response = await apiClient.post('/preferences/time', data);
    return response.data;
  }

  static async updateTimePreference(id: number, data: UpdateTimePreferenceData): Promise<TimePreference> {
    const response = await apiClient.put(`/preferences/time/${id}`, data);
    return response.data;
  }

  static async deleteTimePreference(id: number): Promise<void> {
    await apiClient.delete(`/preferences/time/${id}`);
  }

  // Shift Type Preferences
  static async createShiftTypePreference(data: CreateShiftTypePreferenceData): Promise<ShiftTypePreference> {
    const response = await apiClient.post('/preferences/shift-type', data);
    return response.data;
  }

  static async updateShiftTypePreference(id: number, data: UpdateShiftTypePreferenceData): Promise<ShiftTypePreference> {
    const response = await apiClient.put(`/preferences/shift-type/${id}`, data);
    return response.data;
  }

  static async deleteShiftTypePreference(id: number): Promise<void> {
    await apiClient.delete(`/preferences/shift-type/${id}`);
  }

  // Conflict checking
  static async checkConflicts(data: {
    employee_id: number;
    schedule_date: string;
    start_time: string;
    end_time: string;
    shift_type_id: number;
  }): Promise<PreferenceConflict> {
    const response = await apiClient.post('/preferences/check-conflicts', data);
    return response.data;
  }

  // Preference overrides
  static async createPreferenceOverride(data: CreatePreferenceOverrideData): Promise<PreferenceOverride> {
    const response = await apiClient.post('/preferences/override', data);
    return response.data;
  }

  static async getPreferenceOverrides(scheduleId: number): Promise<PreferenceOverride[]> {
    const response = await apiClient.get(`/preferences/overrides/${scheduleId}`);
    return response.data;
  }

  // Templates
  static async getTemplates(): Promise<PreferenceTemplate[]> {
    const response = await apiClient.get('/preferences/templates');
    return response.data;
  }

  static async createTemplate(data: CreatePreferenceTemplateData): Promise<PreferenceTemplate> {
    const response = await apiClient.post('/preferences/templates', data);
    return response.data;
  }

  // Bulk operations
  static async bulkApplyTemplate(employeeIds: number[], templateId: number): Promise<{ message: string }> {
    const response = await apiClient.post('/preferences/bulk-apply', {
      employee_ids: employeeIds,
      template_id: templateId
    });
    return response.data;
  }

  // Utility methods for preference management
  static async refreshPreferences(employeeId?: number): Promise<EmployeePreferences> {
    if (employeeId) {
      return this.getEmployeePreferences(employeeId);
    } else {
      return this.getMyPreferences();
    }
  }

  static async validateScheduleAgainstPreferences(
    employeeId: number,
    scheduleData: {
      date: string;
      start_time: string;
      end_time: string;
      shift_type_id: number;
    }
  ): Promise<PreferenceConflict> {
    return this.checkConflicts({
      employee_id: employeeId,
      schedule_date: scheduleData.date,
      start_time: scheduleData.start_time,
      end_time: scheduleData.end_time,
      shift_type_id: scheduleData.shift_type_id
    });
  }

  // Helper methods for preference summaries
  static calculatePreferenceSummary(preferences: EmployeePreferences) {
    return {
      total_preferences: 
        preferences.date_preferences.length +
        preferences.day_preferences.length +
        preferences.time_preferences.length +
        preferences.shift_type_preferences.length,
      date_preferences: preferences.date_preferences.length,
      day_preferences: preferences.day_preferences.length,
      time_preferences: preferences.time_preferences.length,
      shift_type_preferences: preferences.shift_type_preferences.length,
      conflicts_count: 0 // This would be calculated based on current schedules
    };
  }

  // Helper methods for preference filtering
  static filterActivePreferences(preferences: EmployeePreferences): EmployeePreferences {
    return {
      date_preferences: preferences.date_preferences,
      day_preferences: preferences.day_preferences.filter(p => p.is_active),
      time_preferences: preferences.time_preferences.filter(p => p.is_active),
      shift_type_preferences: preferences.shift_type_preferences.filter(p => p.is_active)
    };
  }

  static filterPreferencesByPriority(preferences: EmployeePreferences, priority: 'low' | 'medium' | 'high'): EmployeePreferences {
    return {
      date_preferences: preferences.date_preferences.filter(p => p.priority === priority),
      day_preferences: preferences.day_preferences.filter(p => p.priority === priority),
      time_preferences: preferences.time_preferences.filter(p => p.priority === priority),
      shift_type_preferences: preferences.shift_type_preferences.filter(p => p.priority === priority)
    };
  }

  // Helper methods for preference validation
  static validateDatePreference(data: CreateDatePreferenceData): string[] {
    const errors: string[] = [];

    if (!data.preference_date) {
      errors.push('Preference date is required');
    }

    if (!data.preference_type) {
      errors.push('Preference type is required');
    }

    if (data.is_recurring && !data.recurrence_pattern) {
      errors.push('Recurrence pattern is required for recurring preferences');
    }

    return errors;
  }

  static validateDayPreference(data: CreateDayPreferenceData): string[] {
    const errors: string[] = [];

    if (data.day_of_week < 0 || data.day_of_week > 6) {
      errors.push('Day of week must be between 0 and 6');
    }

    if (!data.preference_type) {
      errors.push('Preference type is required');
    }

    if (data.week_of_month && (data.week_of_month < 1 || data.week_of_month > 5)) {
      errors.push('Week of month must be between 1 and 5');
    }

    return errors;
  }

  static validateTimePreference(data: CreateTimePreferenceData): string[] {
    const errors: string[] = [];

    if (!data.start_time) {
      errors.push('Start time is required');
    }

    if (!data.end_time) {
      errors.push('End time is required');
    }

    if (data.start_time && data.end_time && data.start_time >= data.end_time) {
      errors.push('Start time must be before end time');
    }

    if (data.max_daily_hours && (data.max_daily_hours < 1 || data.max_daily_hours > 24)) {
      errors.push('Max daily hours must be between 1 and 24');
    }

    if (data.max_weekly_hours && (data.max_weekly_hours < 1 || data.max_weekly_hours > 168)) {
      errors.push('Max weekly hours must be between 1 and 168');
    }

    return errors;
  }

  static validateShiftTypePreference(data: CreateShiftTypePreferenceData): string[] {
    const errors: string[] = [];

    if (!data.shift_type_id) {
      errors.push('Shift type is required');
    }

    if (!data.preference_type) {
      errors.push('Preference type is required');
    }

    return errors;
  }
} 